import { useEffect, useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { useSession } from '../../../app/useSession';
import { useTeamMember } from '../../../app/useTeamMember';
import { useCompanies } from '../../crm/companies/companiesQueries';
import { ProjectDrawer } from './ProjectDrawer';
import { ProjectList } from './ProjectList';
import { projectStatusLabel, type ProjectRow } from './projects.schema';
import { useProjects } from './projectsQueries';
import styles from './projects.module.css';

export function ProjectsPage() {
  const session = useSession();
  const sessionObj = session.status === 'signed-in' ? session.session : null;
  const userId = sessionObj?.user.id ?? null;
  const { data: teamMember } = useTeamMember(sessionObj);
  const orgId = teamMember?.organization_id ?? null;
  const { data: projects = [], isLoading, error } = useProjects(orgId);
  const { data: companies = [] } = useCompanies(orgId);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<ProjectRow | null>(null);
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  // `?status=quote,active` narrows the list (old /crm/deals links land here).
  const statusFilter = useMemo(
    () => (params.get('status') ?? '').split(',').map((s) => s.trim()).filter(Boolean),
    [params],
  );
  const visibleProjects = useMemo(
    () => (statusFilter.length === 0 ? projects : projects.filter((p) => statusFilter.includes(p.status))),
    [projects, statusFilter],
  );

  // `?new=1` opens the create drawer (Ctrl+K "צור פרויקט").
  const wantsNew = params.get('new') === '1';
  useEffect(() => {
    if (!wantsNew) return;
    setSelectedProject(null);
    setDrawerOpen(true);
    const next = new URLSearchParams(params);
    next.delete('new');
    setParams(next, { replace: true });
  }, [wantsNew, params, setParams]);

  const companyOptions = useMemo(() => companies.map((c) => ({ id: c.id, name: c.name })), [companies]);
  const companyNameById = useMemo(
    () =>
      companies.reduce<Record<string, string>>((acc, company) => {
        acc[company.id] = company.name;
        return acc;
      }, {}),
    [companies],
  );

  function openCreate() {
    setSelectedProject(null);
    setDrawerOpen(true);
  }

  function openEdit(project: ProjectRow) {
    navigate(`/projects/${project.id}`);
  }

  function closeDrawer() {
    setDrawerOpen(false);
    setSelectedProject(null);
  }

  return (
    <>
      <div className={styles.pageHead}>
        <div>
          <h1>פרויקטים</h1>
          <p className={styles.pageSub}>עבודה פעילה — מה נמסר ללקוחות, איפה זה עומד.</p>
        </div>
        <button type="button" className={styles.btnPrimary} onClick={openCreate}>
          <Plus size={16} aria-hidden="true" />
          פרויקט
        </button>
      </div>

      {statusFilter.length > 0 ? (
        <p className={styles.notice}>
          מסונן לפי סטטוס:{' '}
          {statusFilter.map(projectStatusLabel).join(', ')}
          {' · '}
          <Link to="/projects">הצג הכל</Link>
        </p>
      ) : null}

      {isLoading ? <p className={styles.notice}>טוען פרויקטים...</p> : null}
      {error ? <p className={styles.notice}>לא הצלחנו לטעון פרויקטים · נסה שוב</p> : null}

      {!isLoading && !error && projects.length === 0 ? (
        <div className={styles.emptyCard}>
          <div className={styles.emptySmiley} aria-hidden="true">
            <svg viewBox="0 0 64 64" width="44" height="44">
              <circle cx="32" cy="32" r="28" fill="none" />
              <path
                d="M20 25c2-2 6-2 8 0M36 25c2-2 6-2 8 0M22 39c4 5 16 5 20 0"
                fill="none"
                stroke="currentColor"
                strokeWidth="4"
                strokeLinecap="round"
              />
            </svg>
          </div>
          <p>אין פרויקטים עדיין · פתח את הראשון בלחיצה על «+ פרויקט»</p>
          <button type="button" className={styles.btnPrimary} onClick={openCreate}>
            <Plus size={16} aria-hidden="true" />
            פרויקט
          </button>
        </div>
      ) : null}

      {!isLoading && !error && projects.length > 0 ? (
        <ProjectList
          projects={visibleProjects}
          companyNameById={companyNameById}
          onSelect={openEdit}
        />
      ) : null}

      <ProjectDrawer
        open={drawerOpen}
        project={selectedProject}
        companies={companyOptions}
        orgId={orgId}
        userId={userId}
        onClose={closeDrawer}
      />
    </>
  );
}
